import { Upload } from "lucide-react";
import Papa from "papaparse";
import { useDataProvider, useGetIdentity, useRefresh } from "ra-core";
import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toSlug } from "@/lib/toSlug";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";
import type { Company, Contact } from "../types";
import { createImportedContact } from "./createImportedContact";

/**
 * Smart import - bring in a CSV from anywhere (Apollo, ZoomInfo, Seamless,
 * another CRM), map its columns to Obvious fields, create custom fields for
 * columns Obvious doesn't have, and dedupe against existing contacts by email.
 */

type Target =
  | "skip"
  | "first_name"
  | "last_name"
  | "title"
  | "company"
  | "email"
  | "phone"
  | "linkedin_url"
  | "background"
  | `custom:${string}`
  | "create";

const CORE_TARGETS: { id: Target; name: string }[] = [
  { id: "skip", name: "- Skip -" },
  { id: "first_name", name: "First name" },
  { id: "last_name", name: "Last name" },
  { id: "title", name: "Title" },
  { id: "company", name: "Company" },
  { id: "email", name: "Email" },
  { id: "phone", name: "Phone" },
  { id: "linkedin_url", name: "LinkedIn URL" },
  { id: "background", name: "Background / notes" },
  { id: "create", name: "➕ Create custom field" },
];

/** Header aliases seen in Apollo, ZoomInfo, Seamless, HubSpot, Salesforce exports. */
const GUESSES: [RegExp, Target][] = [
  [/^first[ _-]?name$/i, "first_name"],
  [/^last[ _-]?name$/i, "last_name"],
  [/^(job[ _-]?)?title$/i, "title"],
  [/^(company([ _-]?name)?|account([ _-]?name)?|organization)$/i, "company"],
  [/^(work[ _-]?)?e-?mail([ _-]?address)?$/i, "email"],
  [/^(work[ _-]?(direct[ _-]?)?|mobile[ _-]?|direct[ _-]?)?phone([ _-]?number)?$/i, "phone"],
  [/linkedin/i, "linkedin_url"],
  [/^(notes?|background|description)$/i, "background"],
];

const guessTarget = (header: string): Target => {
  for (const [re, target] of GUESSES) if (re.test(header.trim())) return target;
  return "skip";
};

type Dedupe = "skip" | "update" | "always";

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  /** A row's own create/update call failed - the row's error, never silent. */
  errored: number;
  /** Distinct error messages seen, so ten identical failures don't repeat. */
  errors: string[];
  fieldsCreated: string[];
  /** True when the operator stopped the run early - what finished, stayed. */
  stopped?: boolean;
}

interface ImportProgress {
  /** "index" = pre-scan of existing contacts; "rows" = the row loop. */
  phase: "index" | "rows";
  done: number;
  created: number;
  updated: number;
  skipped: number;
}

export const SmartImportButton = () => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();

  const fileRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, Target>>({});
  const [dedupe, setDedupe] = useState<Dedupe>("update");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const cancelRef = useRef(false);

  const customTargets = (config.customFields ?? [])
    .filter((f) => f.object === "contacts" && f.key)
    .map((f) => ({ id: `custom:${f.key}` as Target, name: `${f.label} (custom)` }));

  const onFile = (file: File) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: ({ data, meta }) => {
        const cols = (meta.fields ?? []).filter(Boolean);
        setHeaders(cols);
        setRows(data);
        setMapping(Object.fromEntries(cols.map((c) => [c, guessTarget(c)])));
        setResult(null);
        setFatalError(null);
        setOpen(true);
      },
    });
  };

  const runImport = async () => {
    setRunning(true);
    setFatalError(null);
    cancelRef.current = false;
    setProgress({ phase: "index", done: 0, created: 0, updated: 0, skipped: 0 });
    try {
      const summary: ImportResult = {
        created: 0,
        updated: 0,
        skipped: 0,
        errored: 0,
        errors: [],
        fieldsCreated: [],
      };
      const report = (done: number) =>
        setProgress({
          phase: "rows",
          done,
          created: summary.created,
          updated: summary.updated,
          skipped: summary.skipped,
        });

      // 1. Create custom field definitions for "create" columns
      const newDefs = headers
        .filter((h) => mapping[h] === "create")
        .map((h) => ({
          key: toSlug(h),
          label: h.trim(),
          object: "contacts" as const,
          type: "text" as const,
        }))
        .filter((d) => !(config.customFields ?? []).some((f) => f.key === d.key));
      if (newDefs.length > 0) {
        updateConfig({
          ...config,
          customFields: [...(config.customFields ?? []), ...newDefs],
        });
        summary.fieldsCreated = newDefs.map((d) => d.label);
      }
      const targetFor = (h: string): Target =>
        mapping[h] === "create" ? (`custom:${toSlug(h)}` as Target) : (mapping[h] ?? "skip");

      // 2. Index existing contacts by email for dedupe
      const { data: existing } = await dataProvider.getList<Contact>("contacts", {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: "last_seen", order: "DESC" },
        filter: {},
      });
      const byEmail = new Map<string, Contact>();
      for (const c of existing)
        for (const e of c.email_jsonb ?? [])
          if (e.email) byEmail.set(e.email.toLowerCase(), c);

      // 3. Company cache (find-or-create by name)
      const companyCache = new Map<string, Company>();
      const findOrCreateCompany = async (name: string): Promise<Company> => {
        const key = name.toLowerCase();
        if (companyCache.has(key)) return companyCache.get(key)!;
        const { data: found } = await dataProvider.getList<Company>("companies", {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "name", order: "ASC" },
          filter: { name },
        });
        const company =
          found[0] ??
          (
            await dataProvider.create("companies", {
              data: { name, created_at: new Date().toISOString(), sales_id: identity?.id },
            })
          ).data;
        companyCache.set(key, company as Company);
        return company as Company;
      };

      // 4. Row loop - progress reported every row so long imports never
      // look frozen, and Stop keeps whatever already landed.
      const now = new Date().toISOString();
      report(0);
      let done = 0;
      for (const row of rows) {
        if (cancelRef.current) {
          summary.stopped = true;
          break;
        }
        const record: Record<string, unknown> = {};
        const customFields: Record<string, string> = {};
        let email = "";
        let phone = "";
        let companyName = "";
        for (const h of headers) {
          const target = targetFor(h);
          const raw = (row[h] ?? "").trim();
          if (target === "skip" || raw === "") continue;
          if (target === "email") email = raw;
          else if (target === "phone") phone = raw;
          else if (target === "company") companyName = raw;
          else if (target.startsWith("custom:")) customFields[target.slice(7)] = raw;
          else record[target] = raw;
        }
        const hasIdentity = email || record.first_name || record.last_name;
        if (!hasIdentity) {
          summary.skipped++;
          report(++done);
          continue;
        }

        const existingContact = email ? byEmail.get(email.toLowerCase()) : undefined;
        if (existingContact && dedupe === "skip") {
          summary.skipped++;
          report(++done);
          continue;
        }

        // A single row's failure (a bad custom-field write, an RLS denial,
        // a network blip) must never take down the rest of the batch, and
        // must never vanish silently either - both happened before this.
        try {
          if (companyName) {
            const company = await findOrCreateCompany(companyName);
            record.company_id = company.id;
          }
          if (email && !existingContact)
            record.email_jsonb = [{ email, type: "Work" }];
          if (phone) record.phone_jsonb = [{ number: phone, type: "Work" }];
          if (Object.keys(customFields).length > 0) {
            // custom_fields is one jsonb column per object (never a
            // dynamic per-tenant column) - a PATCH replaces it whole, so
            // merge client-side to keep whatever this row didn't touch.
            record.custom_fields = existingContact
              ? { ...(existingContact.custom_fields ?? {}), ...customFields }
              : customFields;
          }

          if (existingContact && dedupe === "update") {
            await dataProvider.update("contacts", {
              id: existingContact.id,
              data: { ...record, last_seen: now },
              previousData: existingContact,
            });
            summary.updated++;
          } else {
            await createImportedContact(dataProvider, {
              first_name: "",
              last_name: "",
              tags: [],
              first_seen: now,
              last_seen: now,
              sales_id: identity?.id,
              ...record,
            });
            summary.created++;
          }
        } catch (e) {
          summary.errored++;
          const message = e instanceof Error ? e.message : "Row failed to import.";
          if (!summary.errors.includes(message)) summary.errors.push(message);
        }
        report(++done);
      }

      setResult(summary);
      refresh();
    } catch (e) {
      setFatalError(
        e instanceof Error ? e.message : "Import failed unexpectedly.",
      );
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  const mappedCount = headers.filter((h) => (mapping[h] ?? "skip") !== "skip").length;

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
      <Button variant="outline" onClick={() => fileRef.current?.click()}>
        <Upload /> Smart import
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          // While a run is in flight the dialog stays put - Stop is the exit.
          if (!running) setOpen(o);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Smart import</DialogTitle>
            <DialogDescription>
              {rows.length} rows detected. Map each column to a Obvious field -
              unmapped columns are ignored, or create a custom field to keep
              the data.
            </DialogDescription>
          </DialogHeader>

          {fatalError ? (
            <div className="text-sm flex flex-col gap-2">
              <p className="text-base font-medium text-destructive">
                Import failed
              </p>
              <p className="text-destructive">{fatalError}</p>
              <div className="pt-2">
                <Button variant="outline" onClick={() => setFatalError(null)}>
                  Back to mapping
                </Button>
              </div>
            </div>
          ) : result ? (
            <div className="text-sm flex flex-col gap-1">
              <p className="text-base font-medium">
                {result.stopped ? "Import stopped - everything below stayed" : "Import complete"}
              </p>
              <p>{result.created} contacts created</p>
              <p>{result.updated} contacts updated</p>
              <p>{result.skipped} rows skipped</p>
              {result.errored > 0 && (
                <>
                  <p className="text-destructive">
                    {result.errored} row{result.errored === 1 ? "" : "s"} failed to import
                  </p>
                  {result.errors.map((message) => (
                    <p key={message} className="text-xs text-destructive">
                      {message}
                    </p>
                  ))}
                </>
              )}
              {result.fieldsCreated.length > 0 && (
                <p>
                  Custom fields created: {result.fieldsCreated.join(", ")}
                </p>
              )}
              <div className="pt-2">
                <Button onClick={() => setOpen(false)}>Done</Button>
              </div>
            </div>
          ) : progress ? (
            <div className="flex flex-col gap-3 py-2">
              <p className="text-sm font-medium">
                {progress.phase === "index"
                  ? "Checking existing contacts for duplicates…"
                  : `Importing row ${Math.min(progress.done + 1, rows.length)} of ${rows.length}`}
              </p>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-200"
                  style={{
                    width:
                      progress.phase === "index"
                        ? "4%"
                        : `${Math.round((progress.done / Math.max(1, rows.length)) * 100)}%`,
                  }}
                />
              </div>
              <p className="text-sm text-muted-foreground" aria-live="polite">
                {progress.created} created · {progress.updated} updated ·{" "}
                {progress.skipped} skipped
              </p>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    cancelRef.current = true;
                  }}
                >
                  Stop - keep what's imported
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                {headers.map((h) => (
                  <div key={h} className="grid grid-cols-2 gap-3 items-center">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{h}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        e.g. {rows[0]?.[h] || "-"}
                      </p>
                    </div>
                    <Select
                      value={mapping[h] ?? "skip"}
                      onValueChange={(t: Target) =>
                        setMapping((m) => ({ ...m, [h]: t }))
                      }
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[...CORE_TARGETS, ...customTargets].map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 border-t pt-3 text-sm">
                <span className="text-muted-foreground">
                  If a contact with the same email exists:
                </span>
                <Select value={dedupe} onValueChange={(d: Dedupe) => setDedupe(d)}>
                  <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="update">Update it</SelectItem>
                    <SelectItem value="skip">Skip the row</SelectItem>
                    <SelectItem value="always">Import anyway</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={runImport} disabled={running || mappedCount === 0}>
                  {running ? "Importing…" : `Import ${rows.length} rows`}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
